import { ScoringBand, type StudySelectDto } from '@@types/api.types';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { StudyStatusBadge } from '@components/shared/study-status-badge.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import type { FC } from 'react';
import { formatDashboardScore } from './meia-p4-run-cases.util';
import type { BriefStudyVerdict } from './meia-p5-brief.util';
import { getScoringBandLabel, getScoringBandTone } from './meia-scoring-band.util';

type MeiaP5BriefCoverTileProps = {
  missionsCount: number;
  study: StudySelectDto;
  studyVerdict: BriefStudyVerdict;
  threadsLabel?: string;
};

const BAND_PROCEED_WORDING: Readonly<Record<ScoringBand, string>> = {
  [ScoringBand.GREEN]: 'proceed',
  [ScoringBand.PENDING]: 'pending',
  [ScoringBand.RED]: 'rework',
  [ScoringBand.YELLOW]: 'conditional',
};

const bandRecommendation = (band?: ScoringBand | null): string => {
  if (!band || band === ScoringBand.PENDING) return 'Awaiting scoring output';
  return `${getScoringBandLabel(band)} · ${BAND_PROCEED_WORDING[band]}`;
};

export const MeiaP5BriefCoverTile: FC<MeiaP5BriefCoverTileProps> = (props) => {
  const { missionsCount, study, studyVerdict, threadsLabel } = props;

  return (
    <Tile contentClassName="gap-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-sm font-semibold text-gray-950">{study.name}</span>
          {study.keyOperationalProblem && (
            <p className="line-clamp-2 max-w-2xl text-xs leading-relaxed text-gray-600">
              {study.keyOperationalProblem}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ClassificationBadge classification={study.classificationLevel} />
            <StudyStatusBadge status={study.status} />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span>
              {missionsCount} {missionsCount === 1 ? 'mission' : 'missions'}
            </span>
            {threadsLabel && (
              <>
                <span aria-hidden="true">·</span>
                <span>{threadsLabel}</span>
              </>
            )}
            <span aria-hidden="true">·</span>
            <span>Author: {study.ownerName || '—'}</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-xs text-gray-500">Study Verdict (worst-case)</span>
          <span className={cn('font-mono text-3xl font-semibold', getScoringBandTone(studyVerdict.band).textClass)}>
            {formatDashboardScore(studyVerdict.score)}
          </span>
          <Badge
            size="xs"
            variant={getScoringBandTone(studyVerdict.band).badgeVariant}
          >
            {bandRecommendation(studyVerdict.band)}
          </Badge>
        </div>
      </div>
    </Tile>
  );
};
